import { useCallback, useMemo } from 'react'
import type { ComboboxParsedItem, OptionsFilter, SelectProps } from '@mantine/core'
import { Avatar, Box, Group, Text } from '@mantine/core'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import { canShowInAssigneeDropdown } from '@/lib/workspace-user-roles'
import {
  displayUser,
  workspaceUserInitials,
} from '@/components/tickets/TicketMessageThread'
import type { WorkspaceUserRow } from '@/types/api'

type RenderAssigneeOption = NonNullable<SelectProps['renderOption']>

type TicketAssigneeSelectState = {
  users: WorkspaceUserRow[]
  assignedToId?: string | null
  visibleRoles: string[] | null
  isUserOnShift: (userId: string) => boolean
}

function toSearchString(input: unknown): string {
  if (typeof input === 'string') return input
  if (input == null) return ''
  return String(input)
}

export function useTicketAssigneeSelect({
  users,
  assignedToId,
  visibleRoles,
  isUserOnShift,
}: TicketAssigneeSelectState): {
  assigneeOptions: Array<{ value: string; label: string }>
  assigneeUserById: Map<string, WorkspaceUserRow>
  assigneeFilter: OptionsFilter
  renderAssigneeOption: RenderAssigneeOption
} {
  const assigneeOptionsAssignable = useMemo(() => {
    const roleWhitelist =
      visibleRoles && visibleRoles.length
        ? new Set(visibleRoles.map((role) => role.toLowerCase()))
        : null
    const effectiveRole = (user: WorkspaceUserRow) =>
      String(user.custom_role || user.role || '').trim().toLowerCase()
    const rows = users
      .filter((user) => canShowInAssigneeDropdown(user.role))
      .filter((user) => !roleWhitelist || roleWhitelist.has(effectiveRole(user)))
      .map((user) => ({ value: user.id, label: displayUser(user), onShift: isUserOnShift(user.id) }))
      .sort((left, right) => {
        if (left.onShift !== right.onShift) return left.onShift ? -1 : 1
        return left.label.localeCompare(right.label, 'ru', { sensitivity: 'base' })
      })
      .map(({ value, label }) => ({ value, label }))
    return [{ value: '', label: 'Не назначен' }, ...rows]
  }, [users, isUserOnShift, visibleRoles])

  const assigneeOptions = useMemo(() => {
    if (!assignedToId || assigneeOptionsAssignable.some((option) => option.value === assignedToId)) {
      return assigneeOptionsAssignable
    }
    const user = users.find((row) => row.id === assignedToId)
    const label = user ? displayUser(user) : `Пользователь (${assignedToId.slice(0, 8)}…)`
    return [...assigneeOptionsAssignable, { value: assignedToId, label }]
  }, [assigneeOptionsAssignable, assignedToId, users])

  const assigneeUserById = useMemo(() => new Map(users.map((user) => [user.id, user] as const)), [users])

  const assigneeFilter = useCallback<OptionsFilter>(({ options, search, limit }) => {
    const lim = limit ?? 200
    const q = toSearchString(search).trim().toLowerCase()
    const flat = options.filter(
      (option): option is Extract<ComboboxParsedItem, { value: string }> =>
        typeof option === 'object' && option !== null && 'value' in option && !('group' in option),
    )
    if (!q) return flat.slice(0, lim)
    const out: Extract<ComboboxParsedItem, { value: string }>[] = []
    for (const item of flat) {
      if (out.length >= lim) break
      if (toSearchString(item.label).toLowerCase().includes(q)) {
        out.push(item)
        continue
      }
      const value = item.value
      if (!value) continue
      const user = assigneeUserById.get(value)
      if (toSearchString(user?.email).toLowerCase().includes(q)) out.push(item)
    }
    return out
  }, [assigneeUserById])

  const renderAssigneeOption = useCallback<RenderAssigneeOption>(
    ({ option }) => {
      if (option.value === '') {
        return (
          <Group gap={8} wrap="nowrap" align="center" py={2}>
            <Avatar size={26} radius="xl" color="gray" variant="light">
              —
            </Avatar>
            <Text size="xs" c="dimmed">
              {option.label}
            </Text>
          </Group>
        )
      }
      const user = assigneeUserById.get(option.value)
      if (!user) {
        return (
          <Text size="xs" py={2} truncate>
            {option.label}
          </Text>
        )
      }
      const src = resolveOperatorAvatarSrc({
        operator_name: displayUser(user),
        operator_email: user.email,
        avatar_url: user.avatar_url,
      })
      return (
        <Group gap={8} wrap="nowrap" align="center" py={2} w="100%">
          <ShiftPresenceDot userId={option.value} size={7} />
          <Avatar src={src} size={26} radius="xl" color="blue" variant={src ? 'filled' : 'light'}>
            {src ? null : workspaceUserInitials(user)}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="xs" fw={500} truncate lh={1.25}>
              {displayUser(user)}
            </Text>
            {user.email ? (
              <Text size="xs" c="dimmed" truncate lh={1.2}>
                {user.email}
              </Text>
            ) : null}
          </Box>
        </Group>
      )
    },
    [assigneeUserById],
  )

  return {
    assigneeOptions,
    assigneeUserById,
    assigneeFilter,
    renderAssigneeOption,
  }
}
